import React, { useState } from "react";
import { Link } from "react-router";
import { History, Trash2, X, Clock, BookOpen } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useReadingHistory } from "@/hooks/useReadingHistory";
import { FALLBACK_IMAGE } from "@/lib/article";

interface ReadingHistoryDrawerProps {
  trigger?: React.ReactNode;
}

export function ReadingHistoryDrawer({ trigger }: ReadingHistoryDrawerProps) {
  const [open, setOpen] = useState(false);
  const { history, removeItem, clearHistory } = useReadingHistory();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <button
            className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-foreground/80 hover:text-foreground hover:bg-accent rounded-lg transition-colors"
          >
            <History className="h-4 w-4 text-primary" />
            <span>Reading History</span>
          </button>
        )}
      </DialogTrigger>

      <DialogContent className="sm:max-w-md bg-card/95 backdrop-blur-xl border-border/60 shadow-2xl p-6">
        <DialogHeader className="pb-4 border-b border-border/40 flex flex-row items-center justify-between">
          <div>
            <DialogTitle className="text-lg font-bold text-foreground flex items-center gap-2">
              <History className="h-5 w-5 text-primary" />
              Recently Viewed Stories
            </DialogTitle>
            <p className="text-xs text-muted-foreground mt-0.5">
              Stories you've read recently on this device.
            </p>
          </div>
        </DialogHeader>

        <div className="py-3">
          {history.length === 0 ? (
            <div className="py-12 text-center space-y-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto">
                <BookOpen className="h-6 w-6" />
              </div>
              <p className="text-sm font-semibold text-foreground">No reading history yet</p>
              <p className="text-xs text-muted-foreground max-w-xs mx-auto">
                Articles you explore for more than 2 seconds will automatically appear here for quick reference.
              </p>
            </div>
          ) : (
            <div className="space-y-2.5 max-h-[60vh] overflow-y-auto pr-1">
              {history.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-3 p-2.5 rounded-xl border border-border/40 hover:bg-accent/50 transition-colors group"
                >
                  <img
                    src={item.image || FALLBACK_IMAGE}
                    alt={item.title}
                    className="w-14 h-12 rounded-lg object-cover shrink-0 border border-border/40"
                    loading="lazy"
                  />
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/article/${item.slug}`}
                      onClick={() => setOpen(false)}
                      className="text-xs font-bold text-foreground hover:text-primary line-clamp-1 transition-colors"
                    >
                      {item.title}
                    </Link>
                    <div className="flex items-center gap-2 text-[11px] text-muted-foreground mt-1">
                      {item.category && (
                        <span className="font-semibold text-primary">{item.category}</span>
                      )}
                      <span>·</span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {new Date(item.readAt).toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={() => removeItem(item.id)}
                    className="h-8 w-8 min-h-[32px] min-w-[32px] rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all shrink-0"
                    aria-label={`Remove ${item.title} from history`}
                    title="Remove from history"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {history.length > 0 && (
          <div className="pt-3 border-t border-border/40 flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={clearHistory}
              className="text-xs text-destructive hover:text-destructive hover:bg-destructive/10 flex items-center gap-1.5"
            >
              <Trash2 className="h-3.5 w-3.5" /> Clear history
            </Button>

            <Button
              size="sm"
              onClick={() => setOpen(false)}
              className="rounded-full px-5 text-xs font-semibold"
            >
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
